import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, KeyRound, Lock, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password, 4=done
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      toast.success('Se o e-mail existir, você receberá um código em instantes');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar código');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('O código tem 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/verify-reset-code', { email: email.trim().toLowerCase(), code });
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        email: email.trim().toLowerCase(),
        code,
        new_password: newPassword,
      });
      setStep(4);
      toast.success('Senha redefinida com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Voltar ao login
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6" data-testid="forgot-password-card">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                    step >= s ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-0.5 mx-1 ${step > s ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-4" data-testid="forgot-step-email">
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Mail className="w-7 h-7 text-indigo-600" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Esqueci minha senha</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Digite seu e-mail cadastrado e enviaremos um código de verificação.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  data-testid="forgot-email-input"
                  className="rounded-xl"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full rounded-xl"
                data-testid="forgot-request-code"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enviar código
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="forgot-step-code">
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-indigo-100 flex items-center justify-center">
                  <KeyRound className="w-7 h-7 text-indigo-600" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Digite o código</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Enviamos um código de 6 dígitos para <strong>{email}</strong>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Código de verificação</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  data-testid="forgot-code-input"
                  className="rounded-xl text-center text-2xl font-mono tracking-[0.5em]"
                />
                <p className="text-xs text-slate-500 text-center">Válido por 15 minutos</p>
              </div>
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full rounded-xl"
                data-testid="forgot-verify-code"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Verificar código
              </Button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-xs text-slate-500 hover:text-slate-700 hover:underline"
              >
                Não recebeu? Voltar e tentar outro e-mail
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={resetPassword} className="space-y-4" data-testid="forgot-step-new-password">
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Lock className="w-7 h-7 text-indigo-600" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Nova senha</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Crie uma senha forte e fácil de lembrar
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">Nova senha</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  data-testid="forgot-new-password"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirme a nova senha</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="forgot-confirm-password"
                  className="rounded-xl"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !newPassword}
                className="w-full rounded-xl"
                data-testid="forgot-reset-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Redefinir senha
              </Button>
            </form>
          )}

          {step === 4 && (
            <div className="text-center py-6 space-y-4" data-testid="forgot-step-done">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Senha redefinida!</h1>
              <p className="text-sm text-slate-500">
                Sua senha foi alterada com sucesso. Faça login com a nova senha.
              </p>
              <Button onClick={() => navigate('/login')} className="rounded-xl" data-testid="forgot-go-login">
                Ir para o login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
